import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import type { Option as UpstreamOption } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { SUBSCRIPTION_TYPE_TRANSLATION_KEYS } from 'lumen/utils/constants/general-constants';

interface Option extends UpstreamOption {
  id: string;
  type: string;
}

interface HotelAndMarketDropdownsAccountSelectorSignature {
  Args: {};
}

export default class HotelAndMarketDropdownsAccountSelector extends Component<HotelAndMarketDropdownsAccountSelectorSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare navigation: Services['navigation'];
  @service declare router: Services['router'];
  @service('usage-analytics')
  declare usageAnalytics: Services['usage-analytics'];

  @tracked selectedOption: Option | undefined;

  @cached get options(): Option[] {
    const subscriptions = this.navigation.getSubscriptionsForRoute(
      this.router.currentRouteName!,
    );

    const hasDuplicateType = this.navigation.hasDuplicateTypes(subscriptions);

    return subscriptions
      .map((subscription) => {
        const { id, type } = subscription;

        const typeLabel = this.intl.t(SUBSCRIPTION_TYPE_TRANSLATION_KEYS[type]);
        const label = hasDuplicateType ? `${typeLabel} (${id})` : typeLabel;

        return {
          id: id!,
          label,
          type,
          value: id!,
        };
      })
      .sort((a, b) => {
        const comparisionValue = a.label.localeCompare(b.label);

        if (comparisionValue !== 0) {
          return comparisionValue;
        }

        return Number(a.id) - Number(b.id);
      });
  }

  get selectedAccount(): Option | undefined {
    if (this.selectedOption) {
      return this.selectedOption;
    }

    const currentSubscriptionId = this.hotelAndMarketService.hotel?.id;

    const selectedOption = this.options.find(({ id }) => {
      return id === currentSubscriptionId;
    });

    if (selectedOption) {
      return selectedOption;
    }

    return this.options[0];
  }

  @action updateSelectedAccount(selectedOption: Option): void {
    const previousAccountType = this.selectedAccount?.type;

    this.selectedOption = selectedOption;

    const routeName = this.router.currentRouteName!;

    this.usageAnalytics.track(
      routeName.includes('.settings.')
        ? 'Setting View Changed'
        : 'Product View Changed',
      {
        eventTrigger: 'dropdown-menu',
        accountType: selectedOption.type,
        previousAccountType: previousAccountType,
      },
    );

    this.navigation.saveSubscription(routeName, selectedOption.id);

    const targetRoute =
      this.navigation.getPersistedRoute(routeName) ?? routeName;
    this.router.transitionTo(targetRoute, selectedOption.id);
  }

  <template>
    <PrismSelectInput
      @onChange={{this.updateSelectedAccount}}
      @options={{this.options}}
      @value={{this.selectedAccount}}
      data-test-id="account-selector"
    />
  </template>
}
